// Compact container summary shown at the top of the modal.
// Pure presentation — no state, no API calls.

import { Badge, Box, Group, Stack, Text } from '@mantine/core'
import { IconBrandDocker, IconClock } from '@tabler/icons-react'
import { COLOR, SURFACE } from './tokens'
import { formatElapsed, useTicker } from './useTicker'

interface Props {
  containerName: string
  image?: string
  uptime?: string
  instanceId?: string
  /** When provided, replaces the `uptime` badge with a live elapsed-time
   *  counter (re-renders every 1s). Used by LiveView during a running op. */
  operationStartedAt?: string
}

export function ContainerCard({
  containerName,
  image,
  uptime,
  instanceId,
  operationStartedAt,
}: Props) {
  useTicker(operationStartedAt ? 1000 : 60_000)
  const operationElapsed = operationStartedAt
    ? formatElapsed(Date.now() - new Date(operationStartedAt).getTime())
    : null

  return (
    <Box
      p={12}
      style={{
        background: SURFACE.card,
        border: `1px solid ${SURFACE.cardBorder}`,
        borderRadius: 8,
        boxShadow: SURFACE.cardShadow,
      }}
    >
      <Group gap={12} align="center" wrap="nowrap">
        <Box
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: COLOR.brandGradient,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <IconBrandDocker size={20} />
        </Box>
        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Text
            fw={600}
            size="md"
            style={{
              fontFamily: '"Barlow", sans-serif',
              color: SURFACE.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {containerName.replace(/^\//, '')}
          </Text>
          {image && (
            <Text
              size="xs"
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                color: SURFACE.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={image}
            >
              {image}
            </Text>
          )}
        </Stack>
        {operationElapsed ? (
          <Badge
            variant="light"
            color="inovareTeal"
            size="sm"
            leftSection={<IconClock size={12} />}
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
            aria-live="polite"
            title="Tempo decorrido da operação"
          >
            {operationElapsed}
          </Badge>
        ) : uptime ? (
          <Badge variant="light" color="inovareTeal" size="sm">
            {uptime}
          </Badge>
        ) : null}
      </Group>
    </Box>
  )
}
